import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import type { ImageRect } from '../canvas/selectionMath';
import type { Size } from '../canvas/canvasMath';
import {
  DEFAULT_ZOOM_STATE,
  buildZoomRequest,
  hasSourceImage,
  type ZoomFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  /** Selected region in image-space pixels (from KonvaCanvas select mode). */
  selection: ImageRect | null;
  /** Source image dimensions (for normalizing the rect into centerX/Y fractions). */
  imageSize: Size | null;
  onResult?: (result: OperationResult) => void;
  onRequestClearSelection?: () => void;
}

export const ZoomPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  selection,
  imageSize,
  onResult,
  onRequestClearSelection,
}) => {
  const [state, setState] = useState<ZoomFormState>(DEFAULT_ZOOM_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.zoom);

  const hasSource = hasSourceImage(source);
  const hasSelection = !!selection;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to zoom.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildZoomRequest(state, sid, source, selection, imageSize);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSource || !hasSelection;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CenterFocusStrongIcon fontSize="small" />
        <Typography variant="subtitle2">Zoom</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to zoom into.</Alert>
      )}
      {hasSource && !hasSelection && (
        <Alert severity="info">Drag a rectangle on the canvas to choose the zoom region.</Alert>
      )}

      <TextField
        label="Detail prompt (optional)"
        value={state.detailPrompt}
        onChange={e => setState(s => ({ ...s, detailPrompt: e.target.value }))}
        placeholder="What should the AI add in the zoomed region?"
        size="small"
        fullWidth
      />

      <TextField
        label="Zoom factor"
        type="number"
        value={state.zoomFactor}
        onChange={e => setState(s => ({ ...s, zoomFactor: Number(e.target.value) }))}
        size="small"
        inputProps={{ min: 0, max: 16, step: 0.5 }}
        helperText="Leave at 0 to auto-fit the selection"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Zoom failed.'}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <CenterFocusStrongIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Zooming…' : 'Zoom'}
        </Button>
        {onRequestClearSelection && (
          <Button
            variant="outlined"
            size="small"
            onClick={onRequestClearSelection}
            disabled={!hasSelection || mutation.isPending}
          >
            Clear selection
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
